/**
 * ============================================================================
 * NEGOTIATION TIMER COMPONENT
 * ============================================================================
 * 
 * Displays countdown timer for 7-day dispute negotiation period
 */

import React, { useState, useEffect } from 'react';
import { useI18n } from '../../providers';

/**
 * Negotiation Timer Component
 * @param {Object} props
 * @param {string} props.openedAt - ISO date string when dispute was opened
 * @param {Function} props.onExpire - Callback when negotiation period expires
 * @returns {JSX.Element}
 */
const NegotiationTimer = ({ openedAt, onExpire }) => {
  const { formatTimeRemaining } = useI18n();
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    if (!openedAt) return;

    const calculateTimeRemaining = () => {
      const opened = new Date(openedAt);
      const deadline = new Date(opened.getTime() + 7 * 24 * 60 * 60 * 1000); // 7 days
      const now = new Date();
      const diff = deadline - now;

      if (diff <= 0) {
        setExpired(true);
        setTimeRemaining(null);
        if (onExpire) onExpire();
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      setTimeRemaining({ days, hours, minutes });
    };

    calculateTimeRemaining();
    const interval = setInterval(calculateTimeRemaining, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [openedAt, onExpire]);

  if (!openedAt) return null;

  if (expired) {
    return (
      <div className="bg-orange-50 dark:bg-orange-900/20 border-2 border-orange-300 dark:border-orange-700 rounded-xl p-4">
        <div className="flex items-center gap-2">
          <span className="text-xl">⚖️</span>
          <div>
            <p className="font-semibold text-orange-800 dark:text-orange-200">
              Negotiation period expired
            </p>
            <p className="text-sm text-orange-600 dark:text-orange-400">
              Case will be escalated to mediation
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!timeRemaining) return null;

  const { days, hours, minutes } = timeRemaining;
  const totalDays = days + hours / 24;
  const isUrgent = totalDays < 1;
  const isWarning = totalDays < 2;

  return (
    <div
      className={`
        border-2 rounded-2xl p-5 shadow-lg
        transition-all duration-300
        ${
          isUrgent
            ? 'bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 border-red-300 dark:border-red-700 animate-pulse'
            : isWarning
            ? 'bg-gradient-to-br from-orange-50 to-yellow-50 dark:from-orange-900/20 dark:to-yellow-900/20 border-orange-300 dark:border-orange-700'
            : 'bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border-blue-300 dark:border-blue-700'
        }
        animate-fadeInUp
      `}
    >
      <div className="flex items-center gap-4">
        <div
          className={`
            w-14 h-14 rounded-full flex items-center justify-center text-2xl
            ${
              isUrgent
                ? 'bg-red-100 dark:bg-red-900/40'
                : isWarning
                ? 'bg-orange-100 dark:bg-orange-900/40'
                : 'bg-blue-100 dark:bg-blue-900/40'
            }
            animate-bounce
          `}
        >
          ⏱️
        </div>
        <div className="flex-1">
          <p
            className={`
              font-bold mb-2 text-sm uppercase tracking-wide
              ${
                isUrgent
                  ? 'text-red-800 dark:text-red-200'
                  : isWarning
                  ? 'text-orange-800 dark:text-orange-200'
                  : 'text-blue-800 dark:text-blue-200'
              }
            `}
          >
            Negotiation Period
          </p>
          <div className="flex items-baseline gap-2 mb-2">
            <span
              className={`
                text-3xl font-black
                ${
                  isUrgent
                    ? 'text-red-600 dark:text-red-400'
                    : isWarning
                    ? 'text-orange-600 dark:text-orange-400'
                    : 'text-blue-600 dark:text-blue-400'
                }
              `}
            >
              {days}
            </span>
            <span className="text-lg text-gray-600 dark:text-gray-400">days</span>
            <span className="text-gray-400 mx-1">•</span>
            <span
              className={`
                text-2xl font-bold
                ${
                  isUrgent
                    ? 'text-red-600 dark:text-red-400'
                    : isWarning
                    ? 'text-orange-600 dark:text-orange-400'
                    : 'text-blue-600 dark:text-blue-400'
                }
              `}
            >
              {hours}h {minutes}m
            </span>
          </div>
          <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
            {days > 0
              ? `${days} day${days !== 1 ? 's' : ''} remaining to reach agreement`
              : 'Less than 24 hours remaining - Urgent!'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default NegotiationTimer;
